import { useEffect, useState } from "react";
import { useT, type Uebersetzer } from "../i18n";
import { FehlerBand } from "./index";
import { Blattvonunten } from "./Blattvonunten";
import { Deckblatt, useDeckblatt } from "./Deckblatt";
import { Zeilenblatt, Zweizeiler } from "./Handyformen";
import { Zeichen } from "./Zeichen";
import { ORT_LEER, Ortsformular, anschriftVon, kmText, ortFelder } from "./Ortsformular";
import { useOrtAnlegen } from "../daten/haken";
import { Fehler } from "../daten/klient";
import { useTelefon } from "../schale/geraet";
import type { Einsatzort, Kunde } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Einsatzort anlegen — im Deckblatt, ohne die Maske zu verlassen.

   Luca, 16.09.2026: *„Wenn ich in der Schnellerfassung bin und einen
   Kunden auswähle und es noch keinen Einsatzort gibt, dann muss das neue
   Anlegen triggern, sodass ein kleines Pop-Up-Modal aufgeht, in dem ich
   die Daten zu diesem Einsatzort korrekt pflegen kann."*

   Bis dahin legte „… neu anlegen" in der Schnellerfassung nur einen Namen
   im Speicher der Maske ab — mit der Marke „Anschrift fehlt", ohne dass
   irgendetwas in der Akte stand. Jetzt entsteht der Ort in der Akte, mit
   Anschrift und Zufahrt, und steht danach gewählt im Feld.

   **Der erste Ort wird Standard** — wie in der Akte. Beim nächsten Anruf
   desselben Kunden steht er dann vorbelegt (Regel 11).

   Einhängen wie `KundeAnlegen`: `angelegt` bekommt den fertigen Ort, was
   der Aufrufer damit tut, ist seine Sache.

   **Am Telefon kommt es von unten** (18.09.2026) — dieselbe Form wie in der
   Akte (`masken/kunden/Einsatzorte.tsx`) und aus demselben Grund. Es sind
   dieselben sieben Felder; sie in der Akte von unten und in der
   Schnellerfassung als Vollbild zu zeigen, wären zwei Bedienungen für
   dasselbe Formular.
   ══════════════════════════════════════════════════════════════════ */

function fehlerPfad(e: unknown): string {
  return e instanceof Fehler ? e.textPfad : "fehler.unbekannt";
}

/* ══════════════ „Wohin" ist ein Blatt, kein Auswahlfeld — 20.09.2026 ══════════════

   Luca: *„Ich möchte auch beim direkt Vermieten die Möglichkeit haben, eine
   neue Baustelle anzulegen. Das kann ich aktuell noch nicht. … Du machst da
   dann einfach für ‚Für wen' und dann wohin: Machst du auch wieder ein
   Bottom-Sheet mit den Baustellen, und dann machst du unten in der letzten
   Position ‚Neu anlegen'."* Und am Abend noch einmal: *„Die Baustelle kann ich
   auch nicht auswählen, also das fehlt auch komplett."*

   **Was vorher da war:** im Rahmen des Vermietens ein `<select>` — am Telefon
   die Walze des Betriebssystems, ohne Anschrift, ohne Entfernung, und
   „Einsatzort anlegen" als letzter Eintrag der Walze praktisch unsichtbar. Im
   Abschluss der Sammlung (`MerklisteAbschluss.tsx`) Kacheln des Schreibtischs
   in einem Deckblatt — ein zweiter Weg zu derselben Sache, und keiner davon
   in der Form der Handyfassung.

   **Jetzt ein Blatt, an beiden Stellen dasselbe:** die Baustellen des Kunden
   als Zeilen, die Hauptadresse zuerst, „Ab Hof" als eigene Zeile oben (eine
   Wahl, kein Fehlen), der Haken an der gewählten, die Entfernung rechts — und
   als **letzte** Zeile „Einsatzort anlegen", die das Formular darunter
   öffnet. Es steht **hier**, neben dem Anlegen, weil beides zusammen „wohin"
   ist: wählen oder anlegen. Wer es an einer dritten Stelle braucht, hängt es
   ein und baut es nicht nach.

   Die zweite Zeile bleibt einzeilig (kein `satz`): Alle Zeilen messen 60 px
   wie überall in der Handyfassung. Die Entfernung stand bis heute am Ende
   der Anschrift und war auf 390 px das, was abgeschnitten wurde („… · 8,4 km
   a"); jetzt steht sie rechts, wo eine Zahl hingehört. */
export function Ortblatt({ offen, orte, gewaehlt, kunde, schliessen, waehlen, anlegen }: {
  offen: boolean;
  orte: Einsatzort[];
  /** Der Name des gewählten Orts — leer heißt „Ab Hof". */
  gewaehlt: string;
  /** Der Kunde, dessen Baustellen das sind — die zweite Zeile im Kopf. */
  kunde?: string | undefined;
  schliessen: () => void;
  waehlen: (name: string) => void;
  anlegen: () => void;
}) {
  const t = useT();
  /* Die Hauptadresse steht oben — sie ist die, die der Betrieb am häufigsten
     anfährt, und in der Akte steht sie ebenfalls zuerst. Die Reihenfolge der
     übrigen bleibt die der Akte. */
  const sortiert = [...orte].sort((a, b) => Number(b.standard ?? false) - Number(a.standard ?? false));
  const haken = <Zeichen name="haken" />;
  return (
    <Blattvonunten
      offen={offen}
      schliessen={schliessen}
      titel={t("merkliste.schrittOrt")}
      {...(kunde ? { unter: kunde } : {})}
    >
      <Zeilenblatt>
        <Zweizeiler
          beiKlick={() => waehlen("")}
          winkel={false}
          titel={t("vermieten.ortOhne")}
          unter={t("vermietenTelefon.abHofSatz")}
          {...(gewaehlt === "" ? { rechts: haken } : {})}
        />
        {sortiert.map((o) => (
          <Zweizeiler
            key={o.uuid ?? o.name}
            beiKlick={() => waehlen(o.name)}
            winkel={false}
            punkt={anschriftVon(o) ? "ruhig" : "acht"}
            titel={o.name}
            unter={anschriftVon(o) || t("schnell.ortAnschriftFehlt")}
            rechts={(
              <>
                {o.km ? t("kunde.entfernung", { km: kmText(o.km) }) : null}
                {gewaehlt === o.name ? haken : null}
              </>
            )}
          />
        ))}
        {/* Die letzte Zeile, wie Luca sie beschreibt — mit Winkel, weil
            dahinter ein zweites Blatt aufgeht. */}
        <Zweizeiler
          beiKlick={anlegen}
          titel={t("merkliste.ortAnlegen")}
          unter={t("merkliste.ortAnlegenKurz")}
        />
      </Zeilenblatt>
    </Blattvonunten>
  );
}

/** Anschrift und Entfernung einer Baustelle in einer Zeile — für die Zeile „Wohin" in Rahmen und Abschluss. */
export function ortsatzVon(t: Uebersetzer, o: Einsatzort): string {
  const anschrift = anschriftVon(o);
  if (!anschrift) return t("schnell.ortAnschriftFehlt");
  return o.km
    ? t("schnellOrt.mitKm", { anschrift, km: t("kunde.entfernung", { km: kmText(o.km) }) })
    : anschrift;
}

function ImBlatt({ kunde, name, erster, angelegt, schmutzigAendern }: {
  kunde: Kunde;
  name: string;
  erster: boolean;
  angelegt: (ort: Einsatzort) => void;
  schmutzigAendern: (wert: boolean) => void;
}) {
  const { schliessen } = useDeckblatt();
  const anlegen = useOrtAnlegen();
  return (
    <Ortsformular
      form="blatt"
      anfang={{ ...ORT_LEER, bezeichnung: name }}
      laeuft={anlegen.isPending}
      abbrechen={schliessen}
      schmutzigAendern={schmutzigAendern}
      sichern={(e) => anlegen.mutate(
        { nr: kunde.nr, ort: { ...ortFelder(e), standard: erster } },
        { onSuccess: (ort) => { schmutzigAendern(false); angelegt(ort); } },
      )}
      unten={anlegen.error ? <FehlerBand pfad={fehlerPfad(anlegen.error)} fehler={anlegen.error} /> : null}
    />
  );
}

/** Dasselbe Formular am Telefon — im Blatt von unten, mit haftendem Fuß. */
function ImBlattVonUnten({ kunde, name, erster, titel, unter, schliessen, angelegt }: {
  kunde: Kunde;
  name: string;
  erster: boolean;
  titel: string;
  unter: string;
  schliessen: () => void;
  angelegt: (ort: Einsatzort) => void;
}) {
  const anlegen = useOrtAnlegen();
  /* Eigener Stand statt eines durchgereichten: Dieses Blatt lebt nur am
     Telefon und nur, solange es offen ist — der Schlüssel am Aufrufer wirft
     es beim nächsten Kunden ohnehin weg. Zwei Quellen für dieselbe Frage
     wären die teurere Lösung. */
  const [schmutzig, setzeSchmutzig] = useState(false);
  return (
    <Ortsformular
      anfang={{ ...ORT_LEER, bezeichnung: name }}
      laeuft={anlegen.isPending}
      abbrechen={schliessen}
      schmutzigAendern={setzeSchmutzig}
      sichern={(e) => anlegen.mutate(
        { nr: kunde.nr, ort: { ...ortFelder(e), standard: erster } },
        { onSuccess: (ort) => { setzeSchmutzig(false); angelegt(ort); } },
      )}
      unten={anlegen.error ? <FehlerBand pfad={fehlerPfad(anlegen.error)} fehler={anlegen.error} /> : null}
      rahmen={(felder, knoepfe) => (
        <Blattvonunten
          offen
          form="unten"
          /* Wie in der Akte: sieben Felder brauchen mehr als 52 %
             (`masken/kunden/Person.tsx`, „Der Rastpunkt"). */
          rast="gross"
          /* Sieben angefangene Felder dürfen nicht an einem Zug hängen. */
          schmutzig={schmutzig}
          schliessen={schliessen}
          titel={titel}
          unter={unter}
          fuss={knoepfe}
        >
          <div className="ort-blatt ka-blattfelder">{felder}</div>
        </Blattvonunten>
      )}
    />
  );
}

export function OrtAnlegen({ offen, kunde, name = "", erster, schliessen, angelegt }: {
  offen: boolean;
  kunde: Kunde | null;
  /** Was schon unter „Wohin" stand — die Bezeichnung ist dann vorbelegt. */
  name?: string;
  /** Hat der Kunde noch keinen Ort? Dann wird dieser sein Standard. */
  erster: boolean;
  schliessen: () => void;
  angelegt: (ort: Einsatzort) => void;
}) {
  const t = useT();
  const telefon = useTelefon();
  const [schmutzig, setzeSchmutzig] = useState(false);

  // Beim Öffnen ist nichts angefangen — auch nicht, wenn beim letzten Mal
  // etwas darin stand.
  useEffect(() => { if (offen) setzeSchmutzig(false); }, [offen]);

  if (!kunde) return null;

  const untertitel = t(
    erster ? "einsatzort.anlegenUnterErster" : "einsatzort.anlegenUnter",
    { kunde: kunde.name },
  );

  if (telefon) {
    if (!offen) return null;
    return (
      <ImBlattVonUnten
        key={`${kunde.nr}:${name}`}
        kunde={kunde}
        name={name}
        erster={erster}
        titel={t("einsatzort.anlegenTitel")}
        unter={untertitel}
        schliessen={schliessen}
        angelegt={angelegt}
      />
    );
  }

  return (
    <Deckblatt
      offen={offen}
      schliessen={schliessen}
      schmutzig={schmutzig}
      kompakt
      titel={t("einsatzort.anlegenTitel")}
      untertitel={untertitel}
    >
      {/* Der Schlüssel wirft das Formular weg, sobald es für einen anderen
          Kunden oder mit einem anderen Namen aufgeht. */}
      <ImBlatt
        key={`${kunde.nr}:${name}`}
        kunde={kunde}
        name={name}
        erster={erster}
        angelegt={angelegt}
        schmutzigAendern={setzeSchmutzig}
      />
    </Deckblatt>
  );
}
